import React, { useEffect, useRef, useState } from 'react';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useAolStore } from '../../../context/AolContext';
import { useVesselStore } from '../../../context/VesselContext';
import { NoonReportCargoHeatingSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { CargoHeatingTypeEnum, CargoTankTemperatureTypeEnum, NoonReportSectionEnum } from '../../../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import { createSectionData, getBoolValue, getListValue, getNumberValue, hasInvalidFields, hasMissingValues } from '../../../utils/aolFieldsHelper';
import { ErrorText } from '../../shared/error-text/ErrorText.style';
import { ListHeaderStyle } from '../aol-container/AolContainer.style';
import OdfjellInputComponent, { InputChangeData, TimeInputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { EngineListHeader, EngineListRow, EngineListValuesWrapper, EngineListWrapper } from '../engines-section/EnginesSection.style';
import { CargoTemperatureControlWrapper } from './CargoTemperatureControlSection.style';
import { CargoTankTemperature } from '../../../types/ViewModel/CargoTankTemperature';
import CargoTankTemperaturesComponent from './cargo-tank-temperatures/CargoTankTemperatures';
import { ValidatedNoonReport } from '../../../types/ViewModel/ValidatedNoonReport';
import { AolDataItem } from '../../../types/ViewModel/AolItem';
import { AppFeatureEnum } from '../../../enums/appFeatureEnum';
import useAppFeature from '../../../app/useAppFeature';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';

type CargoTemperatureControlVM = {
    heatingFromAdjacentTanks?: boolean;
    directHeating?: boolean;
    cargoCirculatingHeating?: boolean;
    indirectHeating?: boolean;
    waterInletTemperatureHeater1?: number;
    waterOutletTemperatureHeater1?: number;
    waterInletTemperatureHeater2?: number;
    waterOutletTemperatureHeater2?: number;
    circulatingPumpsForCargoHeatingRunningHours1?: number;
    circulatingPumpsForCargoHeatingRunningHours2?: number;

    showDirectHeating?: boolean;
    showCargoCirculatingHeating?: boolean;
    showIndirectHeating?: boolean;
    showCirculatingPump2?: boolean;

    inhibitedCargoTankTemperatures: CargoTankTemperature[];
    heatedAndCooledCargoTankTemperatures: CargoTankTemperature[];
    otherCargoTankTemperatures: CargoTankTemperature[];

    newCargoTankTemperatures: CargoTankTemperature[];

    sectionData?: NoonReportSectionData;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
};

const initialVM: CargoTemperatureControlVM = {
    inhibitedCargoTankTemperatures: [],
    heatedAndCooledCargoTankTemperatures: [],
    otherCargoTankTemperatures: [],
    newCargoTankTemperatures: [],
};

const CargoTemperatureControlSectionComponent: React.FC = () => {
    const { vessel } = useVesselStore();
    const { selectedAolGroup, selectedAolDataItem, aolSyncEvent } = useAolStore();
    const [vm, setVM] = useState<CargoTemperatureControlVM>(initialVM);
    const { saveAolEvent, saveSection } = useAolSaveHandler();
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const [newCargoTemperatures, setNewCargoTemperatures] = useState<CargoTankTemperature[]>([]);
    const [showCargoHeatingDetails, setShowCargoHeatingDetails] = useState<boolean>(false);
    const hasEmptyCargoTemperaturesValuesInhibited = useRef(false);
    const hasEmptyCargoTemperaturesValuesHeatedAndCooled = useRef(false);
    const hasEmptyCargoTemperaturesValuesOther = useRef(false);
    const cargoHeatingNames = ['indirectHeating', 'heatingFromAdjacentTanks', 'directHeating', 'cargoCirculatingHeating'];
    const { isFeatureEnabled } = useAppFeature();
    const { checkIsAolReadOnly } = useReadOnlyUtils();

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.CargoTemperatureControl &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (
                saveAolEvent.type === 'save_section' &&
                !hasEmptyValues &&
                !hasEmptyCargoTemperaturesValuesInhibited.current &&
                !hasEmptyCargoTemperaturesValuesHeatedAndCooled.current &&
                !hasEmptyCargoTemperaturesValuesOther.current
            ) {
                save();
            }
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (!selectedAolDataItem || !vessel) return;

        if (!vm.isInitialized) {
            initVM();
        }
    }, [selectedAolDataItem, vessel]);

    useEffect(() => {
        if (aolSyncEvent && aolSyncEvent.noonReport && aolSyncEvent.type === 'sync_hours') {
            initVM();
        }
    }, [aolSyncEvent]);

    const isReadOnly = checkIsAolReadOnly(vm?.sectionData);

    const initVM = () => {
        if (!selectedAolDataItem) return;
        const noonReport = selectedAolDataItem.noonReport;
        const cargoHeatingType = vessel?.vesselConfigurationV3?.heatingSystemsConfiguration?.cargoHeatingType;
        const cargoTankTemperatures = mergeCargoTankTemperatures(noonReport, selectedAolDataItem);
        cargoTankTemperatures?.forEach(initCargoTankNames);

        const vm: CargoTemperatureControlVM = {
            heatingFromAdjacentTanks: getBoolValue(noonReport?.cargoHeatingSection?.heatingFromAdjacentTanks) ?? false,
            directHeating: getBoolValue(noonReport?.cargoHeatingSection?.directHeating) ?? false,
            cargoCirculatingHeating: getBoolValue(noonReport?.cargoHeatingSection?.cargoCirculatingHeating) ?? false,
            indirectHeating: getBoolValue(noonReport?.cargoHeatingSection?.indirectHeating) ?? false,

            waterInletTemperatureHeater1: getNumberValue(
                noonReport?.cargoHeatingSection?.hotWaterCargoHeaters.find((h) => h.heaterId === 1)?.waterInletTemperature,
                undefined,
                false
            ),
            waterOutletTemperatureHeater1: getNumberValue(
                noonReport?.cargoHeatingSection?.hotWaterCargoHeaters.find((h) => h.heaterId === 1)?.waterOutletTemperature,
                undefined,
                false
            ),
            waterInletTemperatureHeater2: getNumberValue(
                noonReport?.cargoHeatingSection?.hotWaterCargoHeaters.find((h) => h.heaterId === 2)?.waterInletTemperature,
                undefined,
                false
            ),
            waterOutletTemperatureHeater2: getNumberValue(
                noonReport?.cargoHeatingSection?.hotWaterCargoHeaters.find((h) => h.heaterId === 2)?.waterOutletTemperature,
                undefined,
                false
            ),

            circulatingPumpsForCargoHeatingRunningHours1: getNumberValue(
                noonReport?.cargoHeatingSection?.circulatingPumpsForCargoHeating?.find((p) => p.pumpId === 1)?.runningHours,
                undefined,
                true
            ),
            circulatingPumpsForCargoHeatingRunningHours2: getNumberValue(
                noonReport?.cargoHeatingSection?.circulatingPumpsForCargoHeating?.find((p) => p.pumpId === 2)?.runningHours,
                undefined,
                true
            ),

            showDirectHeating: cargoHeatingType === CargoHeatingTypeEnum.Undefined || cargoHeatingType === CargoHeatingTypeEnum.Direct,
            showCargoCirculatingHeating:
                cargoHeatingType === CargoHeatingTypeEnum.Undefined || cargoHeatingType === CargoHeatingTypeEnum.HeaterOnDeck,
            showIndirectHeating: cargoHeatingType === CargoHeatingTypeEnum.Undefined || cargoHeatingType === CargoHeatingTypeEnum.Indirect,
            showCirculatingPump2:
                vessel?.vesselConfigurationV3?.engineConfiguration?.cargoHeatingCirculatingPumps?.numberOfPumps !== undefined &&
                vessel?.vesselConfigurationV3?.engineConfiguration?.cargoHeatingCirculatingPumps?.numberOfPumps >= 2,

            inhibitedCargoTankTemperatures: getInhibitedCargoTankTemperatures(cargoTankTemperatures),
            heatedAndCooledCargoTankTemperatures: getHeatedAndCooledCargoTankTemperatures(cargoTankTemperatures),
            otherCargoTankTemperatures: getOtherCargoTankTemperatures(cargoTankTemperatures),
            newCargoTankTemperatures: newCargoTemperatures,

            sectionData: noonReport?.cargoHeatingSection?.sectionData ?? createSectionData(NoonReportSectionEnum.CargoTemperatureControl),
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isInitialized: true,
        };

        vm.inhibitedCargoTankTemperatures = vm.inhibitedCargoTankTemperatures.sort(compareCargoTankTemperaturesUsingTankName);
        vm.heatedAndCooledCargoTankTemperatures = vm.heatedAndCooledCargoTankTemperatures.sort(compareCargoTankTemperaturesUsingTankName);
        vm.otherCargoTankTemperatures = vm.otherCargoTankTemperatures.sort(compareCargoTankTemperaturesUsingTankName);

        const _hasCargoHeating = cargoHeatingNames.some((c) => vm[c]);

        setHasCargoHeating(_hasCargoHeating);

        setHasEmptyValues(false);
        setVM({ ...vm });
    };

    const setHasCargoHeating = (value: boolean) => {
        if (!selectedAolDataItem) return;

        selectedAolDataItem.aolDataItemProperties.hasCargoHeating = value;
        setShowCargoHeatingDetails(value);
    };

    const compareCargoTankTemperaturesUsingTankId = (a: CargoTankTemperature, b: CargoTankTemperature): number => {
        let res = a.cargoNumber.localeCompare(b.cargoNumber);
        if (res === 0) {
            res = b.tankId - a.tankId;
        }
        return res;
    };

    const compareCargoTankTemperaturesUsingTankName = (a: CargoTankTemperature, b: CargoTankTemperature): number => {
        let res = a.cargoNumber.localeCompare(b.cargoNumber);
        if (res === 0) {
            res = a.tankName?.localeCompare(b.tankName);
        }
        return res;
    };

    const isInList = (cargoTankTemperatures: CargoTankTemperature[], cargoTankTemperature: CargoTankTemperature): boolean => {
        return getFromList(cargoTankTemperatures, cargoTankTemperature) !== undefined;
    };

    const getFromList = (cargoTankTemperatures: CargoTankTemperature[], cargoTankTemperature: CargoTankTemperature): CargoTankTemperature => {
        return cargoTankTemperatures.find((o) => compareCargoTankTemperaturesUsingTankId(o, cargoTankTemperature) === 0);
    };
    const mergeCargoTankTemperatures = (noonReport: ValidatedNoonReport | undefined, aolDataItem: AolDataItem): CargoTankTemperature[] => {
        const list1 = [...(noonReport?.cargoHeatingSection?.cargoTankTemperatures ?? [])];
        const list2 = [...(aolDataItem?.aolItem?.cargoTankTemperatures ?? [])];
        const _newCargoTankTemperatures = [];

        const mergedList = list2.reduce((cargoTankTemperatures: CargoTankTemperature[], cargoTankTemperature: CargoTankTemperature) => {
            const cargoTankTemperatureInNoonReport = getFromList(list1, cargoTankTemperature);
            if (!cargoTankTemperatureInNoonReport) {
                list1.push(cargoTankTemperature);
                if (noonReport?.cargoHeatingSection) {
                    _newCargoTankTemperatures.push(cargoTankTemperature);
                }
            } else {
                cargoTankTemperatureInNoonReport.tradeName = cargoTankTemperature.tradeName;
                cargoTankTemperatureInNoonReport.isCooledCargo = cargoTankTemperature.isCooledCargo;
                cargoTankTemperatureInNoonReport.isHeatedCargo = cargoTankTemperature.isHeatedCargo;
                cargoTankTemperatureInNoonReport.isInhibitedCargo = cargoTankTemperature.isInhibitedCargo;
            }
            return list1;
        }, [] as CargoTankTemperature[]);

        setNewCargoTemperatures(_newCargoTankTemperatures);

        list1.forEach((cargoTankTemperature) => {
            if (!isInList(mergedList, cargoTankTemperature)) {
                mergedList.push(cargoTankTemperature);
            }
        });

        return mergedList;
    };

    const getInhibitedCargoTankTemperatures = (cargoTankTemperatures: CargoTankTemperature[]): CargoTankTemperature[] => {
        return getListValue(cargoTankTemperatures)?.filter((o) => o.isInhibitedCargo);
    };

    const getHeatedAndCooledCargoTankTemperatures = (cargoTankTemperatures: CargoTankTemperature[]): CargoTankTemperature[] => {
        return getListValue(cargoTankTemperatures)?.filter((o) => !o.isInhibitedCargo && (o.isCooledCargo || o.isHeatedCargo));
    };

    const getOtherCargoTankTemperatures = (cargoTankTemperatures: CargoTankTemperature[]): CargoTankTemperature[] => {
        return getListValue(cargoTankTemperatures)?.filter((o) => !o.isInhibitedCargo && !o.isCooledCargo && !o.isHeatedCargo);
    };

    const initCargoTankNames = (cargoTankTemperature: CargoTankTemperature) => {
        cargoTankTemperature.tankName = vessel?.cargoTanks.find((t) => t.tankId === cargoTankTemperature.tankId)?.tankName ?? '???';
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.type === 'checkbox' ? changeObject.event.target.checked : changeObject.event.target.value;

        if (changeObject.event.target.type === 'checkbox') {
            changeObject.dataObject[name] = value;
            if (cargoHeatingNames.some((c) => c === name)) {
                const _hasCargoHeating = typeof value === 'boolean' && value ? value : cargoHeatingNames.some((c) => c !== name && vm[c]);

                setHasCargoHeating(_hasCargoHeating);
            }
        } else {
            changeObject.dataObject[name] = value ? +value : undefined;
        }
        hasInvalidFields(vm, name, changeObject.isValid);
        setVM({ ...vm });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.hours;
        hasInvalidFields(vm, changeObject.name, changeObject.isValid);
        setVM({ ...vm });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const onVmChange = () => {
        setVM({ ...vm });
    };

    const save = () => {
        const ignoreList = [''];
        ignoreList.push('heatingFromAdjacentTanks');
        ignoreList.push('directHeating');
        ignoreList.push('cargoCirculatingHeating');
        ignoreList.push('indirectHeating');
        ignoreList.push('waterInletTemperatureHeater1');
        ignoreList.push('waterOutletTemperatureHeater1');
        ignoreList.push('waterInletTemperatureHeater2');
        ignoreList.push('waterOutletTemperatureHeater2');
        ignoreList.push('circulatingPumpsForCargoHeatingRunningHours1');
        ignoreList.push('circulatingPumpsForCargoHeatingRunningHours2');
        if (hasMissingValues(vm, ignoreList)) {
            setHasEmptyValues(true);
        } else {
            const data = new NoonReportCargoHeatingSectionFactory().createBM(vm);
            if (hasEmptyValues) setHasEmptyValues(false);

            if (!vm.hasInvalidFields) {
                saveSection(data);
            }
        }
    };
    return (
        <>
            {vm && (
                <CargoTemperatureControlWrapper>
                    <>
                        <OdfjellInputComponent
                            isReadOnly={isReadOnly}
                            type={'checkbox'}
                            description={'Heating from adjacent tanks'}
                            dataObject={vm}
                            name={'heatingFromAdjacentTanks'}
                            value={vm.heatingFromAdjacentTanks}
                            onChange={onInputChange}
                        ></OdfjellInputComponent>
                        {vm.showDirectHeating && (
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'checkbox'}
                                description={'Direct heating'}
                                dataObject={vm}
                                name={'directHeating'}
                                value={vm.directHeating}
                                onChange={onInputChange}
                            ></OdfjellInputComponent>
                        )}
                        {vm.showCargoCirculatingHeating && (
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                type={'checkbox'}
                                description={'Cargo circulating heating'}
                                dataObject={vm}
                                name={'cargoCirculatingHeating'}
                                value={vm.cargoCirculatingHeating}
                                onChange={onInputChange}
                            ></OdfjellInputComponent>
                        )}
                        {vm.showIndirectHeating && (
                            <>
                                <OdfjellInputComponent
                                    isReadOnly={isReadOnly}
                                    type={'checkbox'}
                                    description={'Indirect heating'}
                                    dataObject={vm}
                                    name={'indirectHeating'}
                                    value={vm.indirectHeating}
                                    onChange={onInputChange}
                                ></OdfjellInputComponent>
                            </>
                        )}

                        {showCargoHeatingDetails && (
                            <>
                                <EngineListWrapper>
                                    <EngineListRow resetCols={true}>
                                        <ListHeaderStyle>
                                            <EngineListValuesWrapper gridCols={3}>
                                                <EngineListHeader className={'first-header-col'}>
                                                    Hot Water/Thermal Oil Heater for cargo
                                                </EngineListHeader>
                                                <EngineListHeader className={'header-col'}>1</EngineListHeader>
                                                <EngineListHeader className={'header-col'}>2</EngineListHeader>
                                            </EngineListValuesWrapper>
                                        </ListHeaderStyle>
                                    </EngineListRow>
                                    <EngineListRow resetCols={true}>
                                        <EngineListValuesWrapper gridCols={3}>
                                            <div>Water/Thermal Oil Inlet Temperature</div>
                                            <OdfjellInputComponent
                                                isReadOnly={isReadOnly}
                                                type={'number'}
                                                dataObject={vm}
                                                name={'waterInletTemperatureHeater1'}
                                                value={vm.waterInletTemperatureHeater1}
                                                onChange={onInputChange}
                                                min={0}
                                                max={150}
                                                unit={'℃'}
                                            ></OdfjellInputComponent>
                                            <OdfjellInputComponent
                                                isReadOnly={isReadOnly}
                                                type={'number'}
                                                dataObject={vm}
                                                name={'waterInletTemperatureHeater2'}
                                                value={vm.waterInletTemperatureHeater2}
                                                onChange={onInputChange}
                                                min={0}
                                                max={150}
                                                unit={'℃'}
                                            ></OdfjellInputComponent>
                                        </EngineListValuesWrapper>
                                    </EngineListRow>
                                    <EngineListRow resetCols={true}>
                                        <EngineListValuesWrapper gridCols={3}>
                                            <div>Water/Thermal Oil Outlet Temperature</div>
                                            <OdfjellInputComponent
                                                isReadOnly={isReadOnly}
                                                type={'number'}
                                                dataObject={vm}
                                                name={'waterOutletTemperatureHeater1'}
                                                value={vm.waterOutletTemperatureHeater1}
                                                onChange={onInputChange}
                                                min={0}
                                                max={150}
                                                unit={'℃'}
                                            ></OdfjellInputComponent>
                                            <OdfjellInputComponent
                                                isReadOnly={isReadOnly}
                                                type={'number'}
                                                dataObject={vm}
                                                name={'waterOutletTemperatureHeater2'}
                                                value={vm.waterOutletTemperatureHeater2}
                                                onChange={onInputChange}
                                                min={0}
                                                max={150}
                                                unit={'℃'}
                                            ></OdfjellInputComponent>
                                        </EngineListValuesWrapper>
                                    </EngineListRow>
                                </EngineListWrapper>
                                <EngineListWrapper>
                                    <EngineListRow resetCols={true}>
                                        <ListHeaderStyle>
                                            <EngineListValuesWrapper gridCols={3}>
                                                <EngineListHeader className={'first-header-col'}>
                                                    Circulating pumps for cargo heating
                                                </EngineListHeader>
                                                <EngineListHeader className={'header-col'}>1</EngineListHeader>
                                                {vm.showCirculatingPump2 && <EngineListHeader className={'header-col'}>2</EngineListHeader>}
                                            </EngineListValuesWrapper>
                                        </ListHeaderStyle>
                                    </EngineListRow>
                                    <EngineListRow resetCols={true}>
                                        <EngineListValuesWrapper gridCols={3}>
                                            <div>Running hours</div>
                                            <OdfjellInputComponent
                                                isReadOnly={isReadOnly}
                                                type={'time'}
                                                dataObject={vm}
                                                name={'circulatingPumpsForCargoHeatingRunningHours1'}
                                                value={vm.circulatingPumpsForCargoHeatingRunningHours1}
                                                onTimeInputChange={onTimeInputChange}
                                                min={0}
                                                max={selectedAolGroup?.aolHours}
                                            ></OdfjellInputComponent>
                                            {vm.showCirculatingPump2 && (
                                                <OdfjellInputComponent
                                                    isReadOnly={isReadOnly}
                                                    type={'time'}
                                                    dataObject={vm}
                                                    name={'circulatingPumpsForCargoHeatingRunningHours2'}
                                                    value={vm.circulatingPumpsForCargoHeatingRunningHours2}
                                                    onTimeInputChange={onTimeInputChange}
                                                    min={0}
                                                    max={selectedAolGroup?.aolHours}
                                                ></OdfjellInputComponent>
                                            )}
                                        </EngineListValuesWrapper>
                                    </EngineListRow>
                                </EngineListWrapper>
                            </>
                        )}
                    </>
                    {!vessel?.hasOrcaWebSimpleMode &&
                        !isFeatureEnabled({
                            featureEnum: AppFeatureEnum.CargoTempControlSection,
                            date: selectedAolDataItem.reportDate,
                        }) && (
                            <>
                                <CargoTankTemperaturesComponent
                                    vm={vm}
                                    onChange={onVmChange}
                                    hasEmptyValues={hasEmptyCargoTemperaturesValuesInhibited}
                                    type={CargoTankTemperatureTypeEnum.InhibitedCargo}
                                    aolDataItem={selectedAolDataItem}
                                    isReadOnly={isReadOnly}
                                />
                                <CargoTankTemperaturesComponent
                                    vm={vm}
                                    onChange={onVmChange}
                                    hasEmptyValues={hasEmptyCargoTemperaturesValuesHeatedAndCooled}
                                    type={CargoTankTemperatureTypeEnum.HeatedAndCooledCargo}
                                    aolDataItem={selectedAolDataItem}
                                    isReadOnly={isReadOnly}
                                />
                                <CargoTankTemperaturesComponent
                                    vm={vm}
                                    onChange={onVmChange}
                                    hasEmptyValues={hasEmptyCargoTemperaturesValuesOther}
                                    type={CargoTankTemperatureTypeEnum.OtherCargo}
                                    aolDataItem={selectedAolDataItem}
                                    isReadOnly={isReadOnly}
                                />
                            </>
                        )}
                </CargoTemperatureControlWrapper>
            )}
            {(hasEmptyValues || hasEmptyCargoTemperaturesValuesInhibited.current || hasEmptyCargoTemperaturesValuesHeatedAndCooled.current) && (
                <ErrorText>You must complete the section before saving.</ErrorText>
            )}
        </>
    );
};

export default CargoTemperatureControlSectionComponent;
